import { cn } from '@lib/utils';
import { AlertCircle } from 'lucide-react';
import { forwardRef, useId, type ReactNode, type TextareaHTMLAttributes } from 'react';


export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  containerClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, id, className, containerClassName, disabled, rows = 4, ...rest },
  ref,
) {
  const reactId = useId();
  const taId = id ?? reactId;
  const hasError = Boolean(error);

  return (
    <div className={cn('flex w-full flex-col gap-1.5', containerClassName)}>
      {label ? (
        <label htmlFor={taId} className="text-label text-apple-label-secondary">
          {label}
        </label>
      ) : null}
      <div
        className={cn(
          'rounded-lg transition-all duration-200 ease-apple',
          hasError
            ? 'bg-[#FFF1F2] ring-3 ring-[#FDA4AF]/40'
            : 'bg-apple-gray-100 focus-within:bg-white focus-within:ring-3 focus-within:ring-link/20',
          disabled && 'opacity-60',
        )}
      >
        <textarea
          ref={ref}
          id={taId}
          rows={rows}
          disabled={disabled}
          className={cn(
            'w-full resize-y bg-transparent px-4 py-3 text-[15px] text-apple-label',
            'placeholder:text-apple-placeholder outline-none',
            className,
          )}
          aria-invalid={hasError}
          aria-describedby={hint || error ? `${taId}-desc` : undefined}
          {...rest}
        />
      </div>
      {hasError ? (
        <p id={`${taId}-desc`} className="flex items-center gap-1.5 text-[12px] font-medium text-rose-600">
          <AlertCircle size={13} strokeWidth={1.5} />
          {error}
        </p>
      ) : hint ? (
        <p id={`${taId}-desc`} className="text-[12px] text-apple-gray-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
